import React from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';
import { FileText, TrendingUp, Building2, Search, Users } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: TrendingUp,
      title: 'Acquisition & Underwriting Support',
      points: [
        'Pro forma modeling',
        'Returns analysis (IRR, equity multiple, cash-on-cash)',
        'Sensitivity & scenario modeling',
        'Source & use schedules',
        'Investment committee materials'
      ]
    },
    {
      icon: FileText,
      title: 'Lease Abstraction & Rent Roll Validation',
      points: [
        'Investment-grade lease abstracts',
        'Amendment reconciliation',
        'Risk flagging & discrepancy notes',
        'Rent roll validation',
        'Document inventory management'
      ]
    },
    {
      icon: Building2,
      title: 'ARGUS Enterprise Support',
      points: [
        'Property-level cash flow modeling',
        'Rent roll loading and validation',
        'Scenario testing',
        'Assumption audits',
        'Output review and QC'
      ]
    },
    {
      icon: Search,
      title: 'Market Research & Investment Memos',
      points: [
        'Market overviews and trends',
        'Comparable sales and lease analysis',
        'Submarket research',
        'IC-ready investment summaries',
        'Executive briefings'
      ]
    },
    {
      icon: Users,
      title: 'Dedicated Offshore Analyst Pods',
      points: [
        'Long-term embedded analyst support',
        'Time-zone aligned workflows',
        'Customized training on client standards',
        'Integrated team communication',
        'Scalable capacity'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans">
      <Navigation />

      {/* Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            Services
          </h1>
          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            Institutional-grade analysis and support for U.S. commercial real estate investors, 
            private equity firms, and lenders.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-lg border border-gray-200 hover:border-[#4A6FA5] transition-colors"
                >
                  <div className="flex items-start mb-6">
                    <div className="p-3 bg-[#0A2540] rounded mr-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A2540] pt-2">
                      {service.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {service.points.map((point, idx) => (
                      <li key={idx} className="flex items-start text-[#2E2E2E]">
                        <span className="text-[#4A6FA5] mr-3 mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-[#0A2540] mb-6">
            How We Work
          </h2>
          <div className="space-y-4 text-[#2E2E2E] leading-relaxed">
            <p>
              All deliverables are structured to match institutional standards and integrate seamlessly 
              with existing workflows.
            </p>
            <p>
              We provide dedicated analyst resources that operate as an extension of your team — 
              time-zone aligned, trained on your processes, and committed to accuracy and consistency.
            </p>
            <p>
              Our work is designed for acquisitions teams, asset management platforms, lenders, 
              and private equity firms that require institutional-quality output at offshore economics.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
